import React, { useState, useEffect, useRef } from "react";
import { CURRENCY, ITEMSTATUS } from "../../../contants";
import { getPriceByTableType, getTableTypeFromOrder } from "../helpers/orderHelpers";

export const getBgColor = (type, kotQuantity, quantity) => {
  const percentage = (kotQuantity / quantity) * 100;
  if (type === 1) {
    return `linear-gradient(90deg,#ffff8f ${percentage}%, #fff ${percentage}%)`;
  }
  return ITEMSTATUS.find((d) => d.value === type)?.bgColor;
};

// Editable Quantity Input Component with Debounce
const EditableQuantityInput = ({ item, kotIndex, handleQuantityChange }) => {
  const [localQuantity, setLocalQuantity] = useState(item.quantity);
  const [isEditing, setIsEditing] = useState(false);
  const debounceTimerRef = useRef(null);

  // Sync local quantity when item quantity changes from external source (e.g., +/- buttons)
  useEffect(() => {
    if (!isEditing) {
      setLocalQuantity(item.quantity);
    }
  }, [item.quantity, isEditing]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleChange = (e) => {
    let value = e.target.value;

    // Allow empty input for editing
    if (value === "") {
      setLocalQuantity("");
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      return;
    }

    // Validation for numbers and decimals
    let isValid = false;
    if (item?.isFeatured) {
      // Allow numbers like 0.2, .2, 1.25 (up to 2 decimal)
      // More permissive regex to allow typing
      const decimalRegex = /^(\d*)\.?(\d{0,3})$/;
      isValid = decimalRegex.test(value);
    } else {
      // Only digits (integers)
      const integerRegex = /^\d{0,4}$/;
      isValid = integerRegex.test(value);
    }

    if (!isValid) {
      return;
    }

    // Prevent values that are too long
    if (value.length > 6) {
      return;
    }

    setLocalQuantity(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      let numValue;
      if (item?.isFeatured) {
        // Handle decimal values properly
        if (value.startsWith(".")) {
          numValue = parseFloat("0" + value);
        } else if (value.endsWith(".")) {
          // Allow trailing dot but parse without it
          numValue = parseFloat(value.slice(0, -1));
        } else {
          numValue = parseFloat(value);
        }
      } else {
        numValue = parseInt(value, 10);
      }

      // Accept only values >= 0.1 for isFeatured, >= 1 for normal
      const minValue = item?.isFeatured ? 0.1 : 1;
      if (isNaN(numValue) || numValue < minValue) {
        setLocalQuantity(item.quantity);
        return;
      }

      // Round to 2 decimal places for isFeatured items
      if (item?.isFeatured) {
        numValue = Math.round(numValue * 100) / 100;
      }

      const change = numValue - item.quantity;
      handleQuantityChange(item, kotIndex, change);
      
    }, 600);
  };

  const handleBlur = () => {
    setIsEditing(false);
  
    // Clear debounce timer on blur
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  
    let numValue;
    const valueStr = String(localQuantity);
  
    // Handle empty or invalid input
    if (valueStr === "" || valueStr === ".") {
      setLocalQuantity(item.quantity);
      return;
    }
  
    if (item?.isFeatured) {
      if (valueStr.startsWith(".")) {
        numValue = parseFloat("0" + valueStr);
      } else if (valueStr.endsWith(".")) {
        numValue = parseFloat(valueStr.slice(0, -1));
      } else {
        numValue = parseFloat(valueStr);
      }
      // Round to 2 decimal places
      numValue = Math.round(numValue * 100) / 100;
    } else {
      numValue = parseInt(valueStr, 10);
    }
  
    const minValue = item?.isFeatured ? 0.1 : 1;
  
    // If invalid or less than minimum, reset to current quantity
    if (isNaN(numValue) || numValue < minValue) {
      setLocalQuantity(item.quantity);
      return;
    }
  
    // Calculate the change needed and save immediately on blur
    const change = numValue - item.quantity;
    if (Math.abs(change) > 0.001) {
      handleQuantityChange(item, kotIndex, change);
      // Set to the properly rounded value to prevent sync issues
      setLocalQuantity(numValue);
    } else {
      // Reset to the properly rounded current quantity (not the floating-point error version)
      setLocalQuantity(item?.isFeatured ? parseFloat(item.quantity.toFixed(2)) : item.quantity);
    }
  };

  const handleFocus = (e) => {
    setIsEditing(true);
    e.target.select(); // Select all text when focused
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Clear debounce and save immediately
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      e.target.blur(); // Trigger blur to save
    }
    if (e.key === "Escape") {
      // Clear debounce and cancel
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setLocalQuantity(item.quantity); // Reset to original
      e.target.blur();
    }
  };

  return (
    <input
      type="text"
      className="form-control form-control-sm text-center font-weight-bold mx-1 no-spinner"
      style={{
        width: "50px", // Slightly wider to accommodate decimals like "99.5"
        height: "28px",
        padding: "0 4px",
      }}
      value={localQuantity}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      onKeyPress={(e) => {
        // Allow numbers and decimal point
        if (!/[0-9.]/.test(e.key)) {
          e.preventDefault();
        }
      }}
    />
  );
};

const OrderItemsList = ({
  tables,
  currentOrder,
  orderItemsGrouped,
  handleQuantityChange,
  handleDeleteItem,
  onToggleDetailsAccordion,
  isDetailsAccordionOpen,
}) => {
  if (
    orderItemsGrouped.length === 0 ||
    orderItemsGrouped.every((kot) => kot.items.length === 0)
  ) {
    return (
      <div className="text-center text-muted py-5">
        <div style={{ fontSize: "48px", opacity: 0.3 }}>
          <i className="mdi mdi-basket-outline"></i>
        </div>
        <p className="mb-0">No items added yet</p>
      </div>
    );
  }

  // Use the shared helper to get the correct table type
  // This will handle Parcel (P01, P02), Home Delivery (H01, H02), and regular tables
  const tableType = getTableTypeFromOrder(tables, currentOrder);

  return (
    <>
      {orderItemsGrouped.map(
        (kot, kotGroupIndex) =>
          kot.items.length > 0 && (
            <div key={kotGroupIndex} className="mt-2 px-2">
              {/* KOT Header */}
              <div className="bg-purple text-white rounded px-2 py-1 mb-2 font-weight-bold small">
                {kot.kotNumber ? `KOT ${kot.kotNumber}` : "Current KOT"}
              </div>

              {/* KOT Items */}
              {kot.items.map((item, itemIndex) => {
                // Get the correct price based on table type
                // If no mapping exists, this will fall back to itemPrice
                const displayPrice = getPriceByTableType(item, tableType);
                const itemTotal = displayPrice * item.quantity;

                return (
                  <div
                    key={itemIndex}
                    className={`d-flex align-items-center justify-content-between p-2 ${
                      itemIndex === kot.items.length - 1 ? "" : "border-bottom"
                    }`}
                    style={{
                      background: getBgColor(
                        item.itemStatusId,
                        item.kotQuantity,
                        item.quantity
                      ),
                    }}
                  >
                    <div className="flex-grow-1">
                      <div className="font-weight-bold small">
                        {item.itemName}
                        {item?.activeTopping && item.activeTopping.length > 0 && (
                          <span
                            style={{
                              fontWeight: "normal",
                              color: "rgba(0,0,0,0.6)",
                            }}
                          >
                            {" with "}
                            {item.activeTopping
                              .map((t) => t.itemName)
                              .join(" + ")}
                          </span>
                        )}
                      </div>
                      <div className="text-dark small">
                        {CURRENCY}
                        {displayPrice} × {item.quantity?.toFixed(2)} ={" "}
                        {CURRENCY}
                        {itemTotal?.toFixed(2)}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        className="btn btn-outline-dark btn-sm p-0"
                        style={{ width: "28px", height: "28px" }}
                        onClick={() =>
                          handleQuantityChange(item, kot.kotIndex, -1)
                        }
                        disabled={item.quantity <= 1}
                      >
                        <i className="mdi mdi-minus"></i>
                      </button>
                      <EditableQuantityInput
                        item={item}
                        kotIndex={kot.kotIndex}
                        handleQuantityChange={handleQuantityChange}
                      />
                      <button
                        className="btn btn-outline-dark btn-sm p-0"
                        style={{ width: "28px", height: "28px" }}
                        onClick={() =>
                          handleQuantityChange(item, kot.kotIndex, 1)
                        }
                      >
                        <i className="mdi mdi-plus"></i>
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm ml-1 p-0"
                        style={{ width: "28px", height: "28px" }}
                        onClick={() =>
                          handleDeleteItem(item, kot.kotNumber, kot.kotIndex)
                        }
                      >
                        <i className="mdi mdi-delete-outline"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )
      )}
    </>
  );
};

export default OrderItemsList;